import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "space-y-4" }
const _hoisted_2 = { class: "p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl" }
const _hoisted_3 = { class: "flex items-start gap-3" }
const _hoisted_4 = { class: "text-sm text-amber-700 dark:text-amber-300 mt-1" }
const _hoisted_5 = { class: "p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl" }
const _hoisted_6 = { class: "text-sm font-medium text-blue-800 dark:text-blue-200 mb-2 flex items-center gap-2" }
const _hoisted_7 = { class: "text-sm text-blue-700 dark:text-blue-300 space-y-1 ml-6 list-disc" }
const _hoisted_8 = {
  key: 0,
  class: "p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl"
}
const _hoisted_9 = { class: "flex items-start gap-3" }
const _hoisted_10 = { class: "text-sm text-red-700 dark:text-red-300 mt-1" }

import { ref, computed } from 'vue'
import { RefreshCw, AlertTriangle, AlertCircle, Info } from 'lucide-vue-next'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import { employeeService } from '@/services/user'

interface Props {
  isVisible: boolean
}

interface Emits {
  close: []
  success: [message: string]
}

const endMonth = ((44 - 1) % 12) + 1 // Month 8 (August)

export default /*@__PURE__*/_defineComponent({
  __name: 'SyncModuleAccess',
  props: {
    isVisible: { type: Boolean }
  },
  emits: ["close", "success"],
  setup(__props: any, { emit: __emit }) {


const emit = __emit

const currentYear = new Date().getFullYear()

// Calculate end coverage: 44 months from January of current year
const endYear = currentYear + Math.floor((44 - 1) / 12) // +3 years

const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const endMonthLabel = computed(() => monthNames[endMonth - 1])

const syncing = ref(false)
const error = ref<string | null>(null)

const handleClose = () => {
  if (!syncing.value) {
    error.value = null
    emit('close')
  }
}

const handleSync = async () => {
  if (syncing.value) return

  try {
    syncing.value = true
    error.value = null

    const response = await employeeService.syncModuleAccess()
    emit('success', response.message || 'Module access synced successfully')
    emit('close')
  } catch (err: any) {
    error.value = err.message || 'Failed to sync module access. Please try again.'
  } finally {
    syncing.value = false
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseModal, {
    "is-visible": _ctx.isVisible,
    title: "Sync Module Access",
    subtitle: "Update all employees to the current 44-month module access schedule",
    icon: _unref(RefreshCw),
    size: "lg",
    type: "warning",
    "show-footer": true,
    onClose: handleClose
  }, {
    footer: _withCtx(() => [
      _createVNode(BaseButton, {
        text: "Cancel",
        variant: "secondary",
        disabled: syncing.value,
        onClick: handleClose
      }, null, 8, ["disabled"]),
      _createVNode(BaseButton, {
        text: "Sync All",
        variant: "warning",
        icon: _unref(RefreshCw),
        loading: syncing.value,
        "loading-text": "Syncing...",
        onClick: handleSync
      }, null, 8, ["icon", "loading"])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_unref(AlertTriangle), { class: "h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" }),
            _createElementVNode("div", null, [
              _cache[2] || (_cache[2] = _createElementVNode("h4", { class: "text-sm font-medium text-amber-800 dark:text-amber-200" }, "This action will override existing assignments", -1)),
              _createElementVNode("p", _hoisted_4, [
                _cache[0] || (_cache[0] = _createTextVNode(" All existing module access records for every employee will be deleted and replaced with the new 44-month schedule starting from ", -1)),
                _createElementVNode("strong", null, "January " + _toDisplayString(_unref(currentYear)), 1),
                _cache[1] || (_cache[1] = _createTextVNode(". ", -1))
              ])
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("h4", _hoisted_6, [
            _createVNode(_unref(Info), { class: "h-4 w-4" }),
            _cache[3] || (_cache[3] = _createTextVNode(" What will happen ", -1))
          ]),
          _createElementVNode("ul", _hoisted_7, [
            _cache[6] || (_cache[6] = _createElementVNode("li", null, "All existing module access month assignments will be removed", -1)),
            _cache[7] || (_cache[7] = _createElementVNode("li", null, "Each employee will receive 44 months of access", -1)),
            _createElementVNode("li", null, [
              _cache[4] || (_cache[4] = _createTextVNode("Coverage: ", -1)),
              _createElementVNode("strong", null, "Jan " + _toDisplayString(_unref(currentYear)), 1),
              _cache[5] || (_cache[5] = _createTextVNode(" through ", -1)),
              _createElementVNode("strong", null, _toDisplayString(endMonthLabel.value) + " " + _toDisplayString(endYear), 1)
            ])
          ])
        ]),
        (error.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createElementVNode("div", _hoisted_9, [
                _createVNode(_unref(AlertCircle), { class: "h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" }),
                _createElementVNode("div", null, [
                  _cache[8] || (_cache[8] = _createElementVNode("h4", { class: "text-sm font-medium text-red-800 dark:text-red-200" }, "Error", -1)),
                  _createElementVNode("p", _hoisted_10, _toDisplayString(error.value), 1)
                ])
              ])
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["is-visible", "icon"]))
}
}

})